// Lone Rider — Moto Lights Configurator
//
// The shared engine behind the combined MTL4 + MTL9 landing page. One instance of
// this drives every buy point on that page — the top buybox, the popup modal, and
// each section's CTA — so the rider's selection is the same wherever they change it.
//
// WHY THIS EXISTS SEPARATELY FROM lr-make-configurator.js
//
// That file is a PDP engine: it reads variants out of the rendered Liquid, writes
// the chosen option1 back into a hidden native <select>, and hands control to
// theme.Variants, which owns price / gallery / ?variant= / add-to-cart. None of
// that exists on a page template, and more importantly it is bound to ONE product.
// This page has to merge two.
//
// It does NOT re-implement the parsing. Variant titles on these products are a
// single option1 string with five fields in it, and getting that wrong is the whole
// difficulty of this catalogue (two spellings of the bundle, an accented Ténéré, a
// mistyped F850 GS Adventure). lr-make-configurator.js already solved that and
// exports its parser, so this file calls it. There is exactly one parser.
//
// THE MERGE
//
//   mtl4 product -> MTL4-only variants  AND  the MTL4+MTL9 BUNDLE variants
//   mtl9 product -> MTL9-only variants
//
// Both are fetched as JSON at runtime and folded into one catalogue keyed
// make -> model -> light set -> mount. Nothing about the catalogue is hardcoded
// here: every id, price, image and availability flag comes from Shopify.
//
// WHAT IS NOT FROM SHOPIFY: the coming-soon list. See COMING SOON below.
//
// DEPENDS ON (load order matters, see the section Liquid):
//   assets/lr-make-configurator.js  — window.LRMakeConfigurator.parseOptionText
//   theme.js                        — theme.Currency.formatMoney, theme.a11y.trapFocus
//   snippets/cart-drawer.liquid     — listens for `ajaxProduct:added`
(() => {
  'use strict';

  // ---------- Products to merge ----------
  //
  // Order matters only for tie-breaks: when the same make/model/set/mount somehow
  // exists on both products, the first one to claim the slot keeps it.
  //
  // `handles` are tried in order and the first that answers wins, so a product can
  // be renamed without breaking the page: the MTL9 product is moving from mtl9 to
  // mtl-lights, and whichever of the two is live gets used. `key` is the stable name
  // the rest of this file uses (SET_PRODUCT, productImages), whatever the handle is.
  const PRODUCTS = [
    { key: 'mtl4', handles: ['mtl4'] },
    { key: 'mtl9', handles: ['mtl-lights', 'mtl9'] }
  ];

  // ---------- Light sets ----------
  //
  // The three things a rider can buy. `bundle` is deliberately always present in
  // this list even for bikes that have no bundle variant: the UI keeps the choice
  // visible but disabled rather than making it vanish, so the option is discoverable.
  const SET_MTL4 = 'mtl4';
  const SET_MTL9 = 'mtl9';
  const SET_BUNDLE = 'bundle';

  const LIGHT_SETS = [
    { key: SET_MTL4, label: 'MTL4' },
    { key: SET_MTL9, label: 'MTL9' },
    { key: SET_BUNDLE, label: 'MTL4 + MTL9' }
  ];

  // ---------- Mounts ----------
  //
  // Keys are the ones lr-make-configurator.js's mountKey() produces. They are reused
  // verbatim rather than renamed so there is no translation layer between the two
  // files — a mount that file can tell apart, this one can too.
  //
  // Bar Clamp ships with the lights; the other two are upgrades. The order here is
  // the order the UI offers them in.
  const MOUNT_ORDER = ['barclamp', 'undernose', 'crashbar'];

  // Fallback wording, used only if a bike's catalogue text never names the mount.
  // Normally the label is the catalogue's OWN spelling — see rememberMount().
  const MOUNT_FALLBACK_LABELS = {
    barclamp: 'Bar Clamp Mount',
    undernose: 'Under Nose Mount',
    crashbar: 'BMW OEM Crash Bar Aux Light Mount'
  };

  // ---------- Cross-product model aliases ----------
  //
  // A bike only offers all three light sets if mtl4 and mtl9 spell it the same way,
  // year included — otherwise it splits into two Model rows, one with MTL4 + bundle
  // and one with MTL9 only. parseOptionText's MODEL_ALIASES already makes each
  // product agree with ITSELF; this makes the two products agree with each other.
  //
  // Kept here, not in MODEL_ALIASES, so the MTL9 PDP's labels are untouched. Each
  // entry maps a canonical model key onto the key it should share. The row's label
  // is the first product's spelling (mtl4 is ingested first).
  const MODEL_KEY_ALIASES = {
    // mtl9 dates the GSA (2019–2023) — after its own alias — mtl4 dates it (2018–2023).
    f850gsadventure20192023: 'f850gsadventure20182023'
  };

  // ---------- Bare sets (no MotoCAN) ----------
  //
  // The bare light set with no MotoCAN harness — no bike in its title, Bar Clamp
  // only. Matched two ways because either one alone has holes: the SKU rule misses
  // a variant whose SKU was typed differently, the title rule misses one whose
  // MotoCAN field is simply absent.
  const BARE_SET_SKU_RX = /^MTL[49]-SET/i;

  // Which light set a bare-set SKU is. Its title can't say: it reads "Motorcycle
  // Lights Set (2 Pods)", with no MTL number in it.
  const BARE_SET_SKU_KEYS = [
    { rx: /^MTL9-SET/i, key: 'mtl9' },
    { rx: /^MTL4-SET/i, key: 'mtl4' }
  ];

  // The bare sets that ARE offered, as the "Other Brands" make: for riders whose
  // bike has no MotoCAN fitment. Off unless the buybox turns it on — see
  // enableOtherBrand(), which only the lights-combo templates do.
  //
  // The MTL4 bare set is flagged custom.hide_variant, which keeps it off the MTL4
  // PDP. This page reads /products/*.js, which carries no metafields, so this list
  // is the only gate here. MTL4 pods need MotoCAN V2 to work, so the buybox shows a
  // warning when it is picked — see labels.mtl4OtherBrandWarning.
  const OTHER_BRAND = 'Other Brand';
  const OTHER_BRAND_MODEL = 'otherbrand';
  const OTHER_BRAND_SETS = ['mtl9', 'mtl4'];

  // The variant that actually goes in the cart for an "Other Brands" set, when it
  // is not the bare set on the mtl9/mtl4 product. The MTL9 one is the "MTL-SET"
  // variant on the mtl9-old product. Price, image and availability still come from
  // the bare set this page reads, so keep the two priced the same.
  const OTHER_BRAND_CART_VARIANTS = { mtl9: 58814765662582 };

  // ---------- Helpers ----------

  const parser = () => (window.LRMakeConfigurator && window.LRMakeConfigurator.parseOptionText) || null;

  // The theme's money formatter, so this page prints prices exactly as the rest of
  // the store does and follows the shopper's selected currency. Never format money
  // by hand here — multi-currency depends on going through this.
  //
  // The store's money format can carry markup (e.g. <span class='money'>…</span>
  // for currency apps). Callers write this with textContent, so the tags are
  // stripped here — otherwise they print literally.
  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
      return String(theme.Currency.formatMoney(cents, theme.settings.moneyFormat))
        .replace(/<[^>]*>/g, '')
        .trim();
    }
    // Only reached if theme.js failed to load; better than printing raw cents.
    return (cents / 100).toFixed(2);
  }

  // ---------- COMING SOON ----------
  //
  // Shopify is NOT the source of truth for what is released. Inventory for the
  // unreleased bikes is untracked, so Shopify reports `available: true` for them and
  // `variant.available` can never tell them apart.
  //
  // The real list is MID_NOVEMBER_MODELS in lr-make-configurator.js. This file does
  // NOT copy it — it reads it off that module's export, so the list stays a one-line
  // edit in the one place it has always been edited. (The brief called for the list
  // to live in Liquid; it never has. See the note in the handover.)
  //
  // Entries are matched as a SUBSTRING of the canonical model key — lowercased,
  // accents and punctuation stripped — which is how that file matches them too. So
  // 'tenere70020192024' matches "Ténéré 700 (2019–2024)" and '1390superadventure'
  // still matches an R or S suffix.
  //
  // A page may ADD to the list without touching JS, via an optional Liquid block:
  //
  //   <script type="application/json" data-lr-moto-lights-coming-soon>
  //     ["somemodelkey"]
  //   </script>
  //
  // It is empty by default, so rendering the block changes nothing until a key is
  // put in it. It only ever adds; it cannot remove a bike the shared list names.
  function comingSoonKeys(root) {
    // The page's own list, rendered by the section. ALWAYS present, and seeded with
    // the current batch, so this works even if the shared export below is missing.
    let fromLiquid = [];
    const el = (root || document).querySelector('[data-lr-moto-lights-coming-soon]');
    if (el) {
      try {
        const parsed = JSON.parse(el.textContent || '[]');
        if (Array.isArray(parsed)) fromLiquid = parsed;
      } catch (e) {
        console.error('[LR moto lights] coming-soon JSON is not valid JSON', e);
      }
    }

    // The PDP's list, when lr-make-configurator.js is on the page. Merged rather
    // than depended on: a bike added there is picked up here for free, but this page
    // never goes quiet just because that export is absent.
    const shared = (window.LRMakeConfigurator && window.LRMakeConfigurator.MID_NOVEMBER_MODELS) || [];
    if (!shared.length) {
      console.warn(
        '[LR moto lights] LRMakeConfigurator.MID_NOVEMBER_MODELS is empty or missing; ' +
        'using the section\'s own coming-soon list only.'
      );
    }

    const keys = fromLiquid.concat(shared)
      .map((k) => String(k || '').toLowerCase().trim())
      .filter(Boolean);

    // Both sources empty means nothing is flagged, and every unreleased bike would
    // look buyable — the one failure of this feature that costs real money. Say so
    // loudly rather than failing silently.
    if (!keys.length) {
      console.error(
        '[LR moto lights] NO coming-soon keys from either source. Unreleased fitments ' +
        'will appear purchasable. Check the section\'s "Coming soon models" setting.'
      );
    }

    // Deduped — the two sources overlap by design.
    return keys.filter((k, i) => keys.indexOf(k) === i);
  }

  // ---------- Catalogue ----------
  //
  // Shape:
  //   catalogue.makes : Map(makeLabel -> {
  //     label,
  //     models: Map(modelKey -> {
  //       label,            the human-readable fitment, incl. year range
  //       fitment,          "BMW R1300 GS Adventure (2024+)" as the rider reads it
  //       sets: Map(setKey -> {
  //         mounts: Map(mountKey -> entry)
  //       })
  //     })
  //   })
  //
  // entry = the one thing that can be added to cart:
  //   { variantId, productHandle, sku, price, compareAt, available, comingSoon,
  //     image, fitment, setKey, mountKey, mountLabel, lightsLabel }

  function newCatalogue() {
    return {
      makes: new Map(),
      mountLabels: new Map(),
      // Each product's own featured image, for showing something sensible before a
      // bike has been chosen and no variant has resolved yet.
      productImages: new Map(),
      // The catalogue's own wording for the MotoCAN harness ("MotoCAN V2"). Every
      // kit this page sells includes one — the no-MotoCAN base sets are dropped in
      // ingest() — so it is a single value shown as a locked step, exactly as the
      // MTL9 PDP's configurator does it.
      motocanLabel: '',
      // The "Other Brands" make — the bare sets above. Kept OUT of `makes` so
      // nothing that walks the catalogue (fromPrice(), the popup) sees it unless
      // it asks for it.
      otherBrand: null,
      loaded: false
    };
  }

  // Which product a light set's own picture comes from. The bundle has no product of
  // its own — its variants live on mtl4 — so it borrows that one.
  const SET_PRODUCT = { mtl4: 'mtl4', bundle: 'mtl4', mtl9: 'mtl9' };

  // Which of the three sets a variant is. Order is load-bearing:
  //
  // BUNDLE FIRST. A bundle SKU reads "BUNDLE-MTL9-MTL4-..." which also contains
  // "BUNDLE-MTL9", so testing MTL9 first would file every bundle under MTL9-only.
  //
  // The parsed lights key is preferred over the SKU because it is spelling-proof:
  // parseOptionText sorts the light tokens, so "MTL9 set & MTL4 set" and
  // "MTL4 set + MTL9 Set" both key as 'mtl4+mtl9' and land on one row. The SKU is
  // the corroborating signal for a variant whose title is worded unusually.
  function classifySet(parsed, variant) {
    const lights = parsed.keys.lights;
    const sku = String(variant.sku || '').toUpperCase();

    if (lights === 'mtl4+mtl9' || sku.indexOf('BUNDLE-MTL9-MTL4') !== -1) return SET_BUNDLE;
    if (lights === 'mtl4' || sku.indexOf('BUNDLE-MTL4') !== -1) return SET_MTL4;
    if (lights === 'mtl9' || sku.indexOf('BUNDLE-MTL9') !== -1) return SET_MTL9;
    return null; // not one of the three — dropped
  }

  // True for the variants this page does not sell: the bare set with no MotoCAN.
  function isBareSet(parsed, variant) {
    if (BARE_SET_SKU_RX.test(String(variant.sku || ''))) return true;
    // parseOptionText sets this for a "No MotoCAN V2" field.
    if (parsed.noMotocan) return true;
    return false;
  }

  // Keeps the catalogue's own spelling of each mount, first one wins, so the UI
  // labels read like the product and not like our internal keys.
  function rememberMount(catalogue, key, text) {
    if (!key || catalogue.mountLabels.has(key)) return;
    const label = String(text || '').trim();
    if (label) catalogue.mountLabels.set(key, label);
  }

  function mountLabel(catalogue, key) {
    return catalogue.mountLabels.get(key) || MOUNT_FALLBACK_LABELS[key] || key;
  }

  // Files a bare set under the "Other Brands" make, if it is one we offer. It has
  // no bike, so it gets one implicit model the rider is never asked to pick.
  function ingestOtherBrand(catalogue, product, parsed, variant) {
    if (parsed.keys.model) return; // a bike-specific bare set — not offered
    const sku = String(variant.sku || '');
    const match = BARE_SET_SKU_KEYS.filter((b) => b.rx.test(sku))[0];
    if (!match || OTHER_BRAND_SETS.indexOf(match.key) === -1) return;

    const mountKey = parsed.keys.mount;
    if (MOUNT_ORDER.indexOf(mountKey) === -1) return;
    rememberMount(catalogue, mountKey, parsed.mount);

    if (!catalogue.otherBrand) {
      catalogue.otherBrand = {
        label: OTHER_BRAND,
        other: true,
        models: new Map([[OTHER_BRAND_MODEL, {
          key: OTHER_BRAND_MODEL,
          label: '',
          fitment: OTHER_BRAND,
          implicit: true,
          sets: new Map()
        }]])
      };
    }
    const model = catalogue.otherBrand.models.get(OTHER_BRAND_MODEL);
    if (!model.sets.has(match.key)) model.sets.set(match.key, { key: match.key, mounts: new Map() });
    const set = model.sets.get(match.key);
    if (set.mounts.has(mountKey)) return;

    set.mounts.set(mountKey, {
      variantId: OTHER_BRAND_CART_VARIANTS[match.key] || variant.id,
      productHandle: product.handle,
      sku: sku,
      title: variant.option1 || variant.title || '',
      price: variant.price,
      compareAt: variant.compare_at_price || null,
      available: !!variant.available,
      // No bike, so nothing on the coming-soon list can match it.
      comingSoon: false,
      image: (variant.featured_image && variant.featured_image.src) || product.featured_image || null,
      fitment: model.fitment,
      setKey: match.key,
      mountKey: mountKey,
      mountLabel: mountLabel(catalogue, mountKey),
      lightsLabel: parsed.lights,
      // "No MotoCAN V2" — read by the summary, so the notify label says so.
      motocanLabel: parsed.motocan,
      noMotocan: true
    });
  }

  // Folds one product's JSON into the catalogue.
  function ingest(catalogue, product, comingSoon, productKey) {
    const parse = parser();
    if (!parse) return;

    if (product.featured_image) {
      catalogue.productImages.set(productKey, product.featured_image);
    }

    (product.variants || []).forEach((variant) => {
      // These products carry the whole configuration in option1.
      const parsed = parse(variant.option1 || variant.title || '');
      if (!parsed) return;                     // not configurator-shaped
      // No-MotoCAN base set — only ever sold as "Other Brands".
      if (isBareSet(parsed, variant)) { ingestOtherBrand(catalogue, product, parsed, variant); return; }

      const setKey = classifySet(parsed, variant);
      if (!setKey) return;

      const mountKey = parsed.keys.mount;
      if (MOUNT_ORDER.indexOf(mountKey) === -1) return; // unknown mount — drop rather than guess
      rememberMount(catalogue, mountKey, parsed.mount);

      // First spelling wins, same rule as the mount labels.
      if (!catalogue.motocanLabel && parsed.motocan) {
        catalogue.motocanLabel = parsed.motocan;
      }

      // --- make ---
      const makeLabel = parsed.make;
      if (!catalogue.makes.has(makeLabel)) {
        catalogue.makes.set(makeLabel, { label: makeLabel, models: new Map() });
      }
      const make = catalogue.makes.get(makeLabel);

      // --- model ---
      // Keyed canonically so one bike spelled two ways is one row; labelled with the
      // catalogue's own wording so it still reads like a bike.
      const modelKey = MODEL_KEY_ALIASES[parsed.keys.model] || parsed.keys.model;
      if (!modelKey) return; // "Other Brand" kits have no bike — not offered on this page
      if (!make.models.has(modelKey)) {
        make.models.set(modelKey, {
          key: modelKey,
          label: parsed.model,
          fitment: (makeLabel + ' ' + parsed.model).trim(),
          sets: new Map()
        });
      }
      const model = make.models.get(modelKey);

      // --- set ---
      if (!model.sets.has(setKey)) model.sets.set(setKey, { key: setKey, mounts: new Map() });
      const set = model.sets.get(setKey);

      // --- mount ---
      // First variant to claim a slot keeps it. A duplicate here means two variants
      // resolve to the same four answers, which is a catalogue problem, not ours.
      if (set.mounts.has(mountKey)) return;

      set.mounts.set(mountKey, {
        variantId: variant.id,
        productHandle: product.handle,
        sku: variant.sku || '',
        title: variant.option1 || variant.title || '',
        price: variant.price,
        compareAt: variant.compare_at_price || null,
        // Shopify's own flag. Necessary but not sufficient — see resolve().
        available: !!variant.available,
        // The authority for "not released yet". Substring match on the canonical
        // model key, exactly as lr-make-configurator.js matches it.
        comingSoon: comingSoon.some((k) => modelKey.indexOf(k) !== -1),
        image: (variant.featured_image && variant.featured_image.src) || product.featured_image || null,
        fitment: model.fitment,
        setKey: setKey,
        mountKey: mountKey,
        mountLabel: mountLabel(catalogue, mountKey),
        lightsLabel: parsed.lights,
        motocanLabel: parsed.motocan
      });
    });
  }

  // ---------- Loading ----------
  //
  // One shared promise: every instance on the page waits on the same two requests.
  let loadPromise = null;

  function load(root) {
    if (loadPromise) return loadPromise;

    const comingSoon = comingSoonKeys(root);

    // First handle that answers wins; a 404 on one moves on to the next.
    const fetchFirst = (handles) => handles.reduce(
      (prev, handle) => prev.catch(() =>
        fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
          .then((res) => {
            if (!res.ok) throw new Error(handle + ' responded ' + res.status);
            return res.json();
          })
      ),
      Promise.reject(new Error('no handles'))
    );

    loadPromise = Promise.all(
      PRODUCTS.map((p) => fetchFirst(p.handles))
    ).then((products) => {
      const catalogue = newCatalogue();
      products.forEach((product, i) => ingest(catalogue, product, comingSoon, PRODUCTS[i].key));
      // Mount labels are resolved after every product is in, so a mount first seen
      // on mtl9 still labels its mtl4 entries.
      const allMakes = Array.from(catalogue.makes.values());
      if (catalogue.otherBrand) allMakes.push(catalogue.otherBrand);
      allMakes.forEach((make) => make.models.forEach((model) =>
        model.sets.forEach((set) => set.mounts.forEach((entry) => {
          entry.mountLabel = mountLabel(catalogue, entry.mountKey);
        }))
      ));
      catalogue.loaded = true;
      return catalogue;
    });

    return loadPromise;
  }

  // ---------- Selection ----------
  //
  // An instance holds one rider's answers and answers three questions for the UI:
  // what can I offer at each step, what does the current selection resolve to, and
  // what happens when they act on it.
  //
  // Every buy point on the page shares ONE instance, so changing the bike in the
  // popup changes it in the buybox too. Subscribers re-render on every change.
  function createInstance(catalogue) {
    const state = { make: '', model: '', set: '', mount: '' };
    const listeners = [];
    // Whether this instance offers the "Other Brands" make. Off by default; the
    // buybox turns it on from its section setting.
    let otherBrandOn = false;

    const emit = () => listeners.forEach((fn) => {
      try { fn(api.snapshot()); } catch (e) { console.error('[LR moto lights] listener failed', e); }
    });

    const isOtherBrand = () => otherBrandOn && !!catalogue.otherBrand && state.make === OTHER_BRAND;
    const getMake = () => (isOtherBrand() ? catalogue.otherBrand : catalogue.makes.get(state.make)) || null;
    const getModel = () => {
      const make = getMake();
      return (make && make.models.get(state.model)) || null;
    };

    // --- Step options ---

    // Other Brands, when on, always last — it is the fallback, not a brand.
    const makes = () => {
      const list = Array.from(catalogue.makes.values())
        .map((m) => ({ value: m.label, label: m.label }))
        .sort((a, b) => a.label.localeCompare(b.label));
      if (otherBrandOn && catalogue.otherBrand) {
        list.push({ value: OTHER_BRAND, label: OTHER_BRAND, other: true });
      }
      return list;
    };

    // Reverse-alphabetical, which on this catalogue reads as newest-first: R1300 GS
    // and R1250 GS come before F900 and F850, rather than the other way round. The
    // buybox and the popup share this so a bike is never in two different places.
    const models = () => {
      const make = getMake();
      // Other Brands has no bike to choose; its one model is picked implicitly.
      if (!make || make.other) return [];
      return Array.from(make.models.values())
        .map((m) => ({ value: m.key, label: m.label }))
        .sort((a, b) => b.label.localeCompare(a.label));
    };

    // All three sets, always, each carrying whether it actually exists for this
    // bike. The UI keeps a missing bundle visible but disabled with a short note
    // rather than hiding it.
    const sets = () => {
      const model = getModel();
      return LIGHT_SETS.map((set) => {
        const node = model && model.sets.get(set.key);
        return {
          value: set.key,
          label: set.label,
          exists: !!node,
          // Cheapest entry for this set, so the UI can show a "from" price.
          fromPrice: node ? Math.min.apply(null, Array.from(node.mounts.values()).map((e) => e.price)) : null
        };
      });
    };

    // Only the mounts that exist for this bike AND this set.
    const mounts = () => {
      const model = getModel();
      const node = model && model.sets.get(state.set);
      if (!node) return [];
      return MOUNT_ORDER
        .filter((key) => node.mounts.has(key))
        .map((key) => ({
          value: key,
          label: mountLabel(catalogue, key),
          included: key === 'barclamp'
        }));
    };

    // --- Setting answers ---
    //
    // Each step clears the ones below it, then tries to re-answer them: if the new
    // bike offers the set and mount the rider already picked, they keep them rather
    // than being sent back to the start. Anything that no longer exists is dropped.
    function repair() {
      const model = getModel();
      if (!model) { state.set = ''; state.mount = ''; return; }

      if (state.set && !model.sets.get(state.set)) state.set = '';
      if (!state.set) {
        // Nothing chosen: leave it unchosen. The rider picks the light set — it is
        // the actual decision this page is asking them to make.
        state.mount = '';
        return;
      }

      const node = model.sets.get(state.set);
      if (state.mount && !node.mounts.has(state.mount)) state.mount = '';
      if (!state.mount) {
        // Default to the first UPGRADE this bike offers, falling back to the included
        // Bar Clamp when it has none. This matches the MTL9 PDP, where the Mount step
        // is a tick-list with the first upgrade already ticked (upgradeMount() in
        // lr-make-configurator.js) — the two journeys have to land on the same
        // starting configuration or the same bike would quote two different prices.
        const offered = MOUNT_ORDER.filter((k) => node.mounts.has(k));
        const upgrade = offered.filter((k) => k !== 'barclamp')[0];
        state.mount = upgrade || offered[0] || '';
      }
    }

    function set(step, value) {
      if (!(step in state)) return;
      if (state[step] === value) return;

      state[step] = value == null ? '' : String(value);

      // Clear everything downstream, then repair() puts back what still applies.
      if (step === 'make') {
        state.set = ''; state.mount = '';
        // No Model step for Other Brands: answer it so the chain carries on.
        state.model = isOtherBrand() ? OTHER_BRAND_MODEL : '';
      }
      if (step === 'model') { state.set = ''; state.mount = ''; }
      if (step === 'set') { state.mount = ''; }

      repair();
      emit();
    }

    // --- Resolution ---
    //
    // The single answer every buy point renders from.
    //
    //   incomplete  — still missing an answer; no price, no button
    //   buyable     — a real variant, not coming soon, in stock
    //   unavailable — show Notify Me instead of Add to Cart, with `reason`:
    //                   no_variant  this bike never had this combination
    //                   coming_soon on the hardcoded list — NOT released yet
    //                   sold_out    Shopify says unavailable
    //
    // A combination is buyable ONLY if all three hold. Coming-soon is checked
    // before stock because it is the stronger statement and the more accurate one:
    // these variants report `available: true`.
    function resolve() {
      if (!state.make || !state.model || !state.set || !state.mount) {
        return { status: 'incomplete', entry: null, reason: null };
      }

      const model = getModel();
      const node = model && model.sets.get(state.set);
      const entry = node && node.mounts.get(state.mount);

      if (!entry) return { status: 'unavailable', entry: null, reason: 'no_variant' };
      if (entry.comingSoon) return { status: 'unavailable', entry: entry, reason: 'coming_soon' };
      if (!entry.available) return { status: 'unavailable', entry: entry, reason: 'sold_out' };

      return { status: 'buyable', entry: entry, reason: null };
    }

    // Everything the UI needs for one render, so a subscriber never reaches into
    // the catalogue itself.
    function snapshot() {
      const resolved = resolve();
      const entry = resolved.entry;
      const model = getModel();

      return {
        state: { make: state.make, model: state.model, set: state.set, mount: state.mount },
        options: { makes: makes(), models: models(), sets: sets(), mounts: mounts() },
        status: resolved.status,
        reason: resolved.reason,
        entry: entry,
        // Price always reflects the currently resolved variant, in the shopper's
        // currency. Null only when nothing resolved at all (incomplete, or a
        // combination with no variant) — there is simply no price to show.
        //
        // A coming-soon or sold-out variant DOES have a price here. Whether to print
        // it is the section's call; the theme's precedent (.lr-config-waitlisted in
        // custom.css) is to take the price off the page for a bike that cannot be
        // ordered rather than quote one for it.
        price: entry ? entry.price : null,
        priceFormatted: entry ? formatMoney(entry.price) : '',
        compareAt: entry ? entry.compareAt : null,
        compareAtFormatted: entry && entry.compareAt ? formatMoney(entry.compareAt) : '',
        image: entry ? entry.image : null,
        fitment: model ? model.fitment : '',
        // The "Other Brands" kit: no bike, no Model step, no MotoCAN.
        otherBrand: isOtherBrand(),
        // A one-line description of the whole selection. Used as the Notify Me
        // label, so it has to read like something a human chose.
        summary: summary()
      };
    }

    // What the rider is buying, as the catalogue words it. Falls back to the
    // catalogue's common wording before a variant has resolved.
    function currentMotocanLabel() {
      const resolved = resolve();
      return (resolved.entry && resolved.entry.motocanLabel) || catalogue.motocanLabel || '';
    }

    // "BMW R1300 GS Adventure (2024+) — MTL4 + MTL9, MotoCAN V2, Under Nose Mount"
    //
    // The MotoCAN harness is listed even though it was never chosen: it IS part of
    // every kit, and leaving it out made the summary read as though the rider were
    // getting lights and a mount only. Ordered as the variant title orders it —
    // lights, then MotoCAN, then mount.
    function summary() {
      const model = getModel();
      if (!model) return '';
      const parts = [model.fitment];
      const setDef = LIGHT_SETS.filter((s) => s.key === state.set)[0];
      const tail = [];
      if (setDef) tail.push(setDef.label);
      const motocan = currentMotocanLabel();
      if (setDef && motocan) tail.push(motocan);
      if (state.mount) tail.push(mountLabel(catalogue, state.mount));
      if (tail.length) parts.push(tail.join(', '));
      return parts.join(' — ');
    }

    // --- Add to cart ---
    //
    // Never called for an unavailable combination: the UI swaps in the Notify Me
    // form instead, and this refuses anyway as a second line of defence so a stale
    // button can never post a coming-soon variant.
    //
    // On success it dispatches `ajaxProduct:added`, which is what this theme's cart
    // drawer listens for (theme.js rebuilds the cart and opens the drawer;
    // cart-drawer.liquid refreshes the free-shipping bar). Same event the sticky bar
    // and quick-add already use — no second cart implementation on this page.
    function addToCart(quantity) {
      const resolved = resolve();
      if (resolved.status !== 'buyable') {
        return Promise.reject(new Error('Not buyable: ' + resolved.status + '/' + resolved.reason));
      }

      // Defaults to 1 so a caller with no quantity stepper does not have to pass one.
      const qty = Math.max(1, parseInt(quantity, 10) || 1);

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: resolved.entry.variantId, quantity: qty }] })
      })
        .then((res) => res.json().then((body) => {
          // Shopify returns 422 with a description for the real failures
          // (sold out between render and click, cart limits).
          if (!res.ok) throw new Error(body.description || body.message || 'Could not add to cart');
          return body;
        }))
        .then((body) => {
          const added = (body.items && body.items[0]) || body;
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: added } }));
          return added;
        });
    }

    // --- Notify Me ---
    //
    // No new backend. The theme already has one: snippets/waitlist-form.liquid posts
    // to Klaviyo via the submit handler in lr-make-configurator.js, which is bound on
    // `document` and loads on every page. That handler reads WHICH bike out of the
    // [data-lr-config-notice-model] span inside the notice wrapping the form.
    //
    // So all this has to do is keep that span filled with the current selection. The
    // signup is then specific to what the rider chose, and the consent copy, success
    // and error messages stay in Liquid where Weglot can translate them.
    //
    // `scope` is the element containing the notice — the buybox, the popup, or a
    // section CTA. Every one of them gets the same treatment, which is what makes
    // Notify Me work everywhere an unavailable combination can be reached.
    function syncNotify(scope) {
      if (!scope) return;
      const el = scope.querySelector('[data-lr-config-notice-model]');
      if (el) el.textContent = summary();

      // The variant id when there is one, so a coming-soon signup can be tied back
      // to the exact variant. Absent for a combination that has no variant at all.
      const form = scope.querySelector('[data-lr-waitlist]');
      if (form) {
        const resolved = resolve();
        if (resolved.entry) form.setAttribute('data-variant-id', resolved.entry.variantId);
        else form.removeAttribute('data-variant-id');
      }
    }

    // Turns the "Other Brands" make on for this instance. A no-op when the
    // catalogue has no offered bare set.
    function enableOtherBrand() {
      if (otherBrandOn) return;
      otherBrandOn = true;
      emit();
    }

    const api = {
      enableOtherBrand: enableOtherBrand,
      OTHER_BRAND: OTHER_BRAND,
      state: () => ({ make: state.make, model: state.model, set: state.set, mount: state.mount }),
      set: set,
      makes: makes,
      models: models,
      sets: sets,
      mounts: mounts,
      resolve: resolve,
      snapshot: snapshot,
      summary: summary,
      addToCart: addToCart,
      syncNotify: syncNotify,
      formatMoney: formatMoney,
      // The picture to show for a light set before anything has resolved — the
      // product's own featured image. Used by the popup, which opens on a set with
      // no bike chosen yet and would otherwise show an empty frame.
      // What to print in the locked MotoCAN step. Falls back to the catalogue's
      // common wording before anything has resolved.
      motocanLabel: currentMotocanLabel,
      // The lowest price a rider could actually pay for this light set, across every
      // bike in the catalogue.
      //
      // PURCHASABLE ONLY: a variant that is coming soon or out of stock is excluded,
      // because quoting a "from" price nobody can pay is worse than quoting none.
      // That is the same rule resolve() uses, so the number here and the number in
      // the buybox can never disagree.
      //
      // Returns null when the set has nothing buyable at all, which the caller shows
      // as no price rather than as zero.
      fromPrice: (setKey) => {
        let lowest = null;
        catalogue.makes.forEach((make) => {
          make.models.forEach((model) => {
            const node = model.sets.get(setKey);
            if (!node) return;
            node.mounts.forEach((entry) => {
              if (entry.comingSoon || !entry.available) return;
              if (lowest === null || entry.price < lowest) lowest = entry.price;
            });
          });
        });
        return lowest;
      },
      setImage: (setKey) => {
        const handle = SET_PRODUCT[setKey];
        return (handle && catalogue.productImages.get(handle)) || null;
      },
      catalogue: catalogue,
      subscribe: (fn) => {
        listeners.push(fn);
        fn(api.snapshot()); // render immediately, so a late subscriber is never blank
        return () => {
          const i = listeners.indexOf(fn);
          if (i > -1) listeners.splice(i, 1);
        };
      }
    };

    return api;
  }

  // ---------- Public entry point ----------
  //
  // One instance per page, shared by every buy point:
  //
  //   MotoLightsConfigurator.ready().then((config) => {
  //     config.subscribe(render);
  //     config.set('make', 'BMW');
  //   });
  let instancePromise = null;

  function ready(root) {
    if (!instancePromise) {
      instancePromise = load(root).then(createInstance);
      instancePromise.catch((e) => {
        console.error('[LR moto lights] could not load the catalogue', e);
      });
    }
    return instancePromise;
  }

  // A SEPARATE selection, over the same catalogue.
  //
  // The popup gets one of these rather than sharing ready()'s instance. That is the
  // theme's own rule for modals — lr-make-configurator.js keeps quick shop and cart
  // upsell modals out of its peer sync, because "a modal is a separate purchase of
  // the same product, not another view of the one being configured on the page
  // behind it". Opening a popup preset to MTL9 must not silently rewrite the
  // buybox's light set.
  //
  // The catalogue itself is still shared: load() memoises it, so this costs no extra
  // requests however many popups a page has.
  function create(root) {
    return load(root).then(createInstance);
  }

  window.MotoLightsConfigurator = {
    ready: ready,
    create: create,
    // Exposed for the sections built on top of this: labels, ordering and the
    // theme's money formatter, so no section has to restate any of them.
    LIGHT_SETS: LIGHT_SETS,
    MOUNT_ORDER: MOUNT_ORDER,
    formatMoney: formatMoney,
    // The theme's own focus trap, reused for the popup rather than writing a second
    // one. theme.a11y.trapFocus({ container, elementToFocus, namespace }).
    a11y: () => (window.theme && theme.a11y) || null
  };
})();
